import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { trackCartFeedback } from "../utils/klaviyo";

const MIDDLEWARE_URL = (process.env.REACT_APP_MIDDLEWARE_URL || "https://krunchmate-middleware.onrender.com").replace(/\/+$/, "");
const SHOWN_KEY = "km_cart_feedback_shown";

const REASONS = [
  { value: "price", label: "Price" },
  { value: "delivery_time", label: "Delivery time" },
  { value: "unsure_flavours", label: "Not sure about the flavours" },
  { value: "other", label: "Other" },
];

// Asked at most once per browser session
const alreadyAsked = () => {
  try {
    return sessionStorage.getItem(SHOWN_KEY) === "true";
  } catch {
    return true;
  }
};
const markAsked = () => {
  try {
    sessionStorage.setItem(SHOWN_KEY, "true");
  } catch {
    // without storage the question may come back on the next visit
  }
};

/**
 * CartFeedbackModal — one question for someone heading off with a full basket: the pointer leaving
 * through the top of the window, on desktop, while at checkout or with the basket open. Phones have
 * no reliable exit signal, so they're never interrupted. Answers are anonymous.
 */
export default function CartFeedbackModal() {
  const { totalCount, subtotal, open: isCartOpen } = useCart();
  const { pathname, search } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [isSent, setIsSent] = useState(false);
  const firstOptionRef = useRef(null);

  const isAtCheckout = pathname === "/checkout" && !search.includes("status=success");
  const isArmed = totalCount > 0 && (isAtCheckout || isCartOpen);

  useEffect(() => {
    if (!isArmed || alreadyAsked() || !window.matchMedia("(pointer: fine)").matches) return undefined;
    const onMouseOut = (event) => {
      if (event.relatedTarget === null && event.clientY <= 0 && !alreadyAsked()) {
        markAsked();
        setIsOpen(true);
      }
    };
    document.addEventListener("mouseout", onMouseOut);
    return () => document.removeEventListener("mouseout", onMouseOut);
  }, [isArmed]);

  useEffect(() => {
    if (!isOpen) return undefined;
    firstOptionRef.current?.focus();
    const onKey = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  if (!isOpen) return null;

  const send = (chosen, note = "") => {
    setIsSent(true);
    trackCartFeedback(chosen, subtotal);
    fetch(`${MIDDLEWARE_URL}/api/cart-feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason: chosen, comment: note.trim().slice(0, 200), cartPence: Math.round(subtotal * 100) }),
    }).catch(() => {
      // feedback is a nice-to-have; nothing to tell the visitor
    });
  };

  const choose = (value) => {
    setReason(value);
    if (value !== "other") send(value);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60" onClick={() => setIsOpen(false)}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-feedback-title"
        className="relative w-full max-w-md rounded-2xl border border-[#FFFBE5]/15 bg-[#082D33] p-6 text-[#FFFBE5] shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        data-testid="cart-feedback-modal"
      >
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="absolute top-4 right-4 opacity-60 hover:opacity-100 transition-opacity"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {isSent ? (
          <div role="status" className="py-4 text-center">
            <p className="font-display text-xl text-[#F6A81E]">Thank you!</p>
            <p className="mt-2 text-sm opacity-80">That really helps us make KrunchMate better. Your basket is saved for later.</p>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="mt-6 px-6 py-3 bg-[#F6A81E] text-[#1D2528] rounded-full font-bold text-sm uppercase tracking-wider hover:bg-[#f7b43b] transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h2 id="cart-feedback-title" className="font-display text-2xl pr-8">What stopped you from krunching today?</h2>
            <p className="mt-2 text-sm opacity-70">One tap, no email needed.</p>
            <div className="mt-5 grid gap-2">
              {REASONS.map((option, index) => (
                <button
                  key={option.value}
                  ref={index === 0 ? firstOptionRef : undefined}
                  type="button"
                  onClick={() => choose(option.value)}
                  className={`w-full p-3.5 text-left rounded-xl border text-sm transition-colors ${
                    reason === option.value ? "border-[#F6A81E] bg-[#F6A81E]/10" : "border-[#FFFBE5]/20 hover:border-[#F6A81E]/60"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {reason === "other" && (
              <form
                className="mt-4 space-y-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  send("other", comment);
                }}
              >
                <label htmlFor="cart-feedback-comment" className="block text-xs uppercase tracking-widest opacity-60">
                  Tell us more (optional)
                </label>
                <input
                  id="cart-feedback-comment"
                  type="text"
                  maxLength={200}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                  className="w-full p-3 bg-black/30 border border-[#FFFBE5]/20 rounded-xl outline-none focus:border-[#F6A81E] text-sm"
                />
                <button
                  type="submit"
                  className="w-full py-3 bg-[#F6A81E] text-[#1D2528] rounded-full font-bold text-sm uppercase tracking-wider hover:bg-[#f7b43b] transition-colors"
                >
                  Send
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
